import { getUserVisibleMenus } from '@/utils/menu'

const state = {
  menuList: [],
  routes: [],
  permissions: [],
  allMenus: [] // 存储所有菜单，用于权限过滤
}

const mutations = {
  SET_MENU_LIST: (state, menuList) => {
    state.menuList = menuList
  },
  SET_ROUTES: (state, routes) => {
    state.routes = routes
  },
  SET_PERMISSIONS: (state, permissions) => {
    state.permissions = permissions
  },
  SET_ALL_MENUS: (state, allMenus) => {
    state.allMenus = allMenus
  }
}

const actions = {
  // 根据用户权限过滤菜单
  filterMenusByUserPermissions({ commit, state }, userInfo) {
    return new Promise((resolve) => {
      if (!userInfo || !state.allMenus.length) {
        commit('SET_MENU_LIST', [])
        resolve([])
        return
      }
      
      const visibleMenus = getUserVisibleMenus(state.allMenus, userInfo)
      commit('SET_MENU_LIST', visibleMenus)
      resolve(visibleMenus)
    })
  },

  // 生成动态路由
  generateRoutes({ commit }, menuData) {
    return new Promise((resolve) => {
      const routes = []
      const permissions = []
      
      // 处理菜单数据，生成路由
      const processMenu = (menu, parentPath = '') => {
        if (menu.menuType === 1) {
          // 一级菜单
          if (menu.children && menu.children.length > 0) {
            // 有子菜单的一级菜单
            const children = []
            menu.children.forEach(child => {
              const childRoute = processMenu(child, parentPath)
              if (childRoute) {
                children.push(childRoute)
              }
            })
            
            if (children.length > 0) {
              routes.push({
                path: `/${menu.menuCode.toLowerCase()}`,
                component: () => import('@/layout/index.vue'),
                meta: {
                  title: menu.menuName,
                  icon: getMenuIcon(menu.menuCode),
                  menuCode: menu.menuCode,
                  menuType: menu.menuType
                },
                children: children
              })
            }
          } else if (menu.menuPage) {
            // 没有子菜单但有页面的菜单
            routes.push({
              path: `/${menu.menuCode.toLowerCase()}`,
              component: () => import(`@/views/${menu.menuPage}/index.vue`),
              meta: {
                title: menu.menuName,
                icon: getMenuIcon(menu.menuCode),
                menuCode: menu.menuCode,
                menuType: menu.menuType
              }
            })
          }
        } else if (menu.menuType === 2) {
          // 二级菜单
          if (menu.menuPage) {
            // 特殊处理设备管理页面的路径映射
            let component
            if (menu.menuPage === 'orgAdmin') { // Added for orgAdmin
              component = () => import('@/views/org/orgAdmin/index.vue')
            } else if (menu.menuPage === 'equAgent') { // Added for equAgent
              component = () => import('@/views/org/equAgent/index.vue')
            } else if (menu.menuPage === 'equAgentCps') { // Added for equAgentCps
              component = () => import('@/views/org/equAgentCps/index.vue')
            } else if (menu.menuPage === 'equRouteConfig') { // Added for equRouteConfig
              component = () => import('@/views/org/equRouteConfig/index.vue')
            } else if (menu.menuPage === 'equUser') { // Added for equUser
              component = () => import('@/views/setting/equUser/index.vue')
            } else if (menu.menuPage === 'dashboard') { // Added for dashboard
              component = () => import('@/views/org/dashboard/index.vue')
            } else if (menu.menuPage === 'equipment') { // Added for equipment
              component = () => import('@/views/org/equipment/index.vue')
            } else if (menu.menuPage === 'filterRecord') { // Added for filterRecord
              component = () => import('@/views/org/filterRecord/index.vue')
            } else if (menu.menuPage === 'waterCard') { // Added for waterCard
              component = () => import('@/views/org/waterCard/index.vue')
            } else if (menu.menuPage === 'status') { // Added for status
              component = () => import('@/views/org/status/index.vue')
            } else if (menu.menuPage === 'waterTotal') { // Added for waterTotal
              component = () => import('@/views/org/waterTotal/index.vue')
            } else if (menu.menuPage === 'waterRecord') { // Added for waterRecord
              component = () => import('@/views/org/waterRecord/index.vue')
            } else {
              component = () => import(`@/views/${menu.menuPage}/index.vue`)
			}
            
            const route = {
              path: menu.menuCode.toLowerCase(),
              name: menu.menuCode,
              component,
              meta: {
                title: menu.menuName,
                icon: getMenuIcon(menu.menuCode),
                menuCode: menu.menuCode,
                menuType: menu.menuType
              }
            }
            
            // 收集权限
            if (menu.actions && menu.actions.length > 0) {
              menu.actions.forEach(action => {
                permissions.push(action.menuCode)
              })
            }
            
            return route
          }
        } else if (menu.menuType === 3) {
          // 按钮权限
          permissions.push(menu.menuCode)
        }
        
        return null
      }
      
      // 处理菜单数据
      if (menuData && menuData.responseMenus) {
        menuData.responseMenus.forEach(menu => {
          processMenu(menu)
        })
      }
      
      // 添加首页路由
      routes.unshift({
        path: '/',
        redirect: '/dashboard',
        component: () => import('@/layout/index.vue'),
        children: [
          {
            path: 'dashboard',
            name: 'Dashboard',
            component: () => import('@/views/org/dashboard/index.vue'),
            meta: {
              titleKey: 'orgMenu.home',
              icon: 'home-filled',
              menuCode: 'INDEX',
              menuType: 1
            }
          }
        ]
      })
      
      // 保存所有菜单用于权限过滤
      commit('SET_ALL_MENUS', menuData.responseMenus || [])
      
      commit('SET_ROUTES', routes)
      commit('SET_PERMISSIONS', permissions)
      commit('SET_MENU_LIST', menuData.responseMenus || [])
      
      resolve(routes)
    })
  }
}

// 根据菜单代码获取图标
function getMenuIcon(menuCode) {
  const iconMap = {
    'INDEX': 'home-filled',
    'DATAM': 'monitor',
    'EQUM': 'cpu',
    'LVXM': 'refresh',
    'UIVERSION': 'upload',
    'DATAQ': 'search',
    'HEART': 'heart',
    'CUSTOM': 'user',
    'EQUACT': 'data-line',
    'CRMM': 'office-building',
    'ORGM': 'user',
    'AGENTCHECK': 'check',
    'AGENTCPS': 'money',
    'AGENTROUTE': 'guide',
    'PARTY_A': 's-grid',
    'MQTTM': 'connection',
    'STATUSM': 'view',
    'OUTORDERM': 'document',
    'DEBUGER': 'tools',
    'WATERTTL': 'water',
    'ACCTM': 'setting',
    'ACCTMA': 'user',
    'TYPEM': 's-grid',
    'SETM': 'tools',
    'VOLMAP': 'map-location',
    'WATER_CONFIG': 'setting',
    'BUSSLOG': 'document',
    'WX_PAY': 'money',
    'WX_PAY_ORDER': 'list',
    'WX_PAY_STAT': 'data-analysis',
    'WX_PAY_WATER_CONFIG': 'setting',
    'WX_PAY_WATER_RECHARGE': 'wallet'
  }
  return iconMap[menuCode] || 'menu'
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
